import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useRead from "../../../hooks/useRead";
import { numberWithComma } from "../../../utility";
import PrimaryLoader from "../../../components/common/PrimaryLoader";

// New Scheme is the only active scheme ("old" default kept for reference).
// const EmployeeCTCDetail = ({ scheme_type = "old" }) => {
const EmployeeCTCDetail = ({ scheme_type = "new" }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { list } = useRead({
    url: "getEmployeeCurrentSalary",
    initialData: {
      employee_id: id,
      scheme_type: scheme_type,
    },
  });

  const handleNavigateToEmployeeDetail = () => {
    const mode = searchParams.get("mode") || "edit";
    navigate(
      `/employee-salary/${id}?type=current_type&scheme=${scheme_type}&mode=${mode}`
    );
  };

  // Function to format numbers with commas
  const displayValue = (value) => {
    if (value === 0 || value === "0") return "0";
    if (!value && value !== 0) return "0";
    return numberWithComma(value);
  };

  const currentSalary = list.data?.data?.[0];

  // Frontend dynamic calculations for Total incl. and Total Excl.
  const basicMonthly = Number(currentSalary?.basic_monthly) || 0;
  const daMonthly = Number(currentSalary?.da_monthly) || 0;
  const totalInclusiveMonthly = basicMonthly + daMonthly;
  const totalInclusiveYearly = totalInclusiveMonthly * 12;

  const hraMonthly = Number(currentSalary?.hra_monthly) || 0;
  const statBonusMonthly = Number(currentSalary?.stat_bonus_monthly) || 0;
  const employerEpfMonthly = Number(currentSalary?.employer_epf_monthly) || 0;
  const totalExclusiveMonthly = hraMonthly + statBonusMonthly + employerEpfMonthly;
  const totalExclusiveYearly = totalExclusiveMonthly * 12;

  const calculatedMonthlyCTC = totalInclusiveMonthly + totalExclusiveMonthly;
  const calculatedAnnualCTC = calculatedMonthlyCTC * 12;

  return (
    <>
      {list.isLoading && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-50 bg-[#ffffffc7]">
          <PrimaryLoader />
        </div>
      )}
      <div className="max-w-4xl px-8 py-8">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="font-semibold text-xl">Salary Detail</h1>
            {searchParams.get("mode") !== "resigned" && currentSalary && (
              <button
                className="bg-color1 text-white px-6 text-xs py-2 rounded-md"
                onClick={handleNavigateToEmployeeDetail}
              >
                Edit
              </button>
            )}
          </div>
          <div className="flex items-center space-x-16 mt-5">
            <div className="space-y-2">
              <p className="text-gray-500 text-sm">ANNUAL CTC</p>
              <h2 className="text-gray-800 font-bold text-lg">
                ₹ {displayValue(currentSalary?.annual_ctc || (scheme_type === "new" ? calculatedAnnualCTC : 0))} per year
              </h2>
            </div>
            <div className="space-y-2">
              <p className="text-gray-500 text-sm">MONTHLY CTC</p>
              <h2 className="text-gray-800 font-bold text-lg">
                ₹ {displayValue(currentSalary?.monthly_ctc || (scheme_type === "new" ? calculatedMonthlyCTC : 0))} per month
              </h2>
            </div>
            {/* {scheme_type === "new" && (
              <div className="space-y-2">
                <p className="text-gray-500 text-sm">BONUS ELIGIBILITY</p>
                <div className="pt-1">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      currentSalary?.is_bonus_eligible
                        ? "bg-green-100 text-green-800 border border-green-200"
                        : "bg-gray-100 text-gray-600 border border-gray-200"
                    }`}
                  >
                    {currentSalary?.is_bonus_eligible ? "Eligible" : "Not Eligible"}
                  </span>
                </div>
              </div>
            )} */}
          </div>
        </div>

        <div className="border border-gray-200 rounded shadow-lg mt-10 px-5">
          <table className="table-auto w-full">
            <thead>
              <tr className="text-gray-600 border-b border-gray-300">
                <th className="w-1/6 min-w-[160px] text-xs font-semibold py-4 lg:py-4 px-3 lg:px-4 text-left">
                  SALARY COMPONENTS
                </th>
                <th className="w-1/6 min-w-[160px] text-xs font-semibold py-4 lg:py-4 px-3 lg:px-4 text-right">
                  MONTHLY AMOUNT
                </th>
                <th className="w-1/6 min-w-[160px] text-xs font-semibold py-4 lg:py-4 px-3 lg:px-4 text-right">
                  ANNUAL AMOUNT
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-3 font-semibold py-4">Earnings</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td className="px-3 font-semibold py-4">
                  <span className="text-sm font-semibold">Basic</span>
                </td>
                <td className="text-right">
                  <p>₹ {displayValue(currentSalary?.basic_monthly)}</p>
                </td>
                <td className="text-right">
                  <p>₹ {displayValue(currentSalary?.basic_yearly)}</p>
                </td>
              </tr>

              {scheme_type === "new" ? (
                // New Scheme Breakdown (matches Payout Wages table)
                <>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">DA</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.da_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.da_yearly)}</p>
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-3 font-bold py-4">
                      <span className="text-sm font-bold">Total incl.</span>
                    </td>
                    <td className="text-right font-bold">
                      <p>₹ {displayValue(totalInclusiveMonthly)}</p>
                    </td>
                    <td className="text-right font-bold">
                      <p>₹ {displayValue(totalInclusiveYearly)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">HRA</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.hra_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.hra_yearly)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Stat Bonus</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.stat_bonus_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.stat_bonus_yearly || (statBonusMonthly * 12))}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Employer PF</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.employer_epf_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.employer_epf_yearly || (employerEpfMonthly * 12))}</p>
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-3 font-bold py-4">
                      <span className="text-sm font-bold">Total Excl.</span>
                    </td>
                    <td className="text-right font-bold">
                      <p>₹ {displayValue(totalExclusiveMonthly)}</p>
                    </td>
                    <td className="text-right font-bold">
                      <p>₹ {displayValue(totalExclusiveYearly)}</p>
                    </td>
                  </tr>
                  {/* Legacy new scheme fields kept commented out to avoid code deletion */}
                  {/*
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Conveyance</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.conveyance_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.conveyance_yearly)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Other Allowance</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.other_allowance_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.other_allowance_yearly)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Variable / Fixed</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.variable_fixed_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.variable_fixed_yearly)}</p>
                    </td>
                  </tr>
                  */}
                </>
              ) : currentSalary?.previous_year ? (
                // Old Scheme: Previous Year Fields
                <>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Conveyance</span>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹ {displayValue(currentSalary?.conveyance_monthly)}
                      </p>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹ {displayValue(currentSalary?.conveyance_yearly)}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">HRA</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.hra_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.hra_yearly)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Others</span>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.other_allowance_monthly
                        )}
                      </p>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.other_allowance_yearly
                        )}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Variable OT</span>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.variable_fixed_monthly
                        )}
                      </p>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.variable_fixed_yearly
                        )}
                      </p>
                    </td>
                  </tr>
                </>
              ) : (
                // Old Scheme: Current Year Fields
                <>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">DA</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.da_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.da_yearly)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">HRA</span>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.hra_monthly)}</p>
                    </td>
                    <td className="text-right">
                      <p>₹ {displayValue(currentSalary?.hra_yearly)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">TA</span>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.travel_allowance_monthly
                        )}
                      </p>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.travel_allowance_yearly
                        )}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">WA</span>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.washing_allowance_monthly
                        )}
                      </p>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.washing_allowance_yearly
                        )}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">SA</span>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.supplementary_allowance_monthly
                        )}
                      </p>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.supplementary_allowance_yearly
                        )}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 font-semibold py-4">
                      <span className="text-sm font-semibold">Variable OT</span>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.variable_fixed_monthly
                        )}
                      </p>
                    </td>
                    <td className="text-right">
                      <p>
                        ₹{" "}
                        {displayValue(
                          currentSalary?.variable_fixed_yearly
                        )}
                      </p>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-300">
                <td className="px-3 font-semibold py-4">Gross Salary</td>
                <td>
                  <p className="text-right font-semibold text-xl">
                    ₹ {displayValue(currentSalary?.monthly_gross || (scheme_type === "new" ? calculatedMonthlyCTC : 0))}
                  </p>
                </td>
                <td>
                  <p className="text-right font-semibold text-xl">
                    ₹ {displayValue(currentSalary?.annual_gross || (scheme_type === "new" ? calculatedAnnualCTC : 0))}
                  </p>
                </td>
              </tr>
              <tr className="border-t border-gray-300">
                <td className="px-3 font-semibold py-4">CTC</td>
                <td className="text-right font-semibold text-xl">
                  <p>₹ {displayValue(currentSalary?.monthly_ctc || (scheme_type === "new" ? calculatedMonthlyCTC : 0))}</p>
                </td>
                <td className="text-right font-semibold text-xl">
                  <p>₹ {displayValue(currentSalary?.annual_ctc || (scheme_type === "new" ? calculatedAnnualCTC : 0))}</p>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
};

export default EmployeeCTCDetail;
